import * as React from "react"
import { Link } from "gatsby"
import { Icon } from "semantic-ui-react"
import he from "he"
import styled from "styled-components"
import UnderlineButton from "../button/UnderlineButton"
import { largeDesktop } from "../../constants/screenSizes"
import { white } from "../../constants/colors"
import { UrlImage } from "components"
import { cleanUrl, safeHeDecode } from "../../util/generalUtil"
import { MenuItemType } from "../../model/MenuItemType"

const FeatureContainer = styled.div`
  width: 320px;
  height: 100%;
  @media (max-width: ${largeDesktop}px) {
    display: none;
  }
`

const FeatureContainerInner = styled.div`
  width: 380px;
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  overflow: hidden;

  img {
    object-fit: cover;
    height: 100%;
    width: 100%;
  }
`

const LinkBox = styled.div`
  background-color: ${white};
  position: absolute;
  right: 0;
  bottom: 30px;
  padding-left: 15px;
  padding-right: 5px;
  text-transform: uppercase;
  font-size: 0.7em;
  text-align: right;
`

const ItemSubContainer = styled.div`
  display: none;
  margin-top: -1px;
  z-index: 10;
`

const ItemSubContentContainer = styled.div`
  position: absolute;
  margin-top: 1px;
  left: 0;
  right: 0;
  z-index: 31;
  display: flex;
  justify-content: center;
  pointer-events: none;
`

const ItemSubContentSubContainer = styled.div`
  display: flex;
  justify-content: space-around;
  height: 300px;
  padding: 30px 30px;
  background-color: ${white};
  z-index: 2;
  width: 100%;
  max-width: 1300px;
  pointer-events: all;
  position: relative;
  box-shadow: 0 0 5px 2px rgba(0, 0, 0, 0.3);
  clip-path: inset(0px -20px -20px -20px);
`

const ColumnContainer = styled.div`
  max-width: 260px;
  width: 100%;
  line-height: 1.5em;
`

const HeaderLink = styled.div`
  margin-bottom: 5px;
  text-transform: uppercase;
  font-size: 0.9em;
  letter-spacing: 1.5px;
`

const ChildLinkContainer = styled.div`
  font-size: 0.9em;
`

const ChildLink = styled(UnderlineButton)`
  letter-spacing: 1px;
  text-transform: capitalize;
  font-size: 0.9em;
`

const FeaturedUnderlineButton = styled(UnderlineButton)`
  margin-right: 5px;
`

const TopLink = styled(UnderlineButton)``

const MenuItemContainer = styled.div<{ isHovered: boolean; isSmall: boolean }>`
  padding: 0;
  flex-grow: 1;
  ${TopLink} {
    text-transform: uppercase;
    font-size: 0.7em;
    letter-spacing: 2px;
  }
  ${({ isHovered }) =>
    isHovered
      ? `
          ${ItemSubContainer} {
            display: block;
          }
      
          ${TopLink} {
            border-bottom: 2px solid hsla(0, 0%, 0%, 0.7);
          }
        `
      : ""}
  ${({ isSmall }) =>
    isSmall
      ? `
          position: relative;
          
          ${ItemSubContentContainer} {
            justify-content: left;
            left: unset;
            width: max-content;
            
            ${ItemSubContentSubContainer} {
              height: 200px;
            }
          }
        `
      : ""};
  &:first-of-type {
    padding-left: 0;
    ${TopLink} {
      padding-left: 0;
    }
  }
  &:last-of-type {
    padding-right: 0;
    ${TopLink} {
      padding-right: 0;
    }
  }
`

const renderMegaMenu = (subLevels: MenuItemType[]) =>
  subLevels.length > 1 || subLevels[0].children.length > 0

const findSubLevels = (currentLevel: MenuItemType, previousLevel: MenuItemType) => {
  if (currentLevel.children.length === 0) {
    return [previousLevel]
  }

  let subLevels = []
  currentLevel.children.forEach(childItem => {
    findSubLevels(childItem, currentLevel).forEach(subLevel => {
      subLevels = [...subLevels, subLevel]
    })
  })

  subLevels = [...Array.from(new Set(subLevels))]

  return subLevels
}

interface MenuItemProps {
  openedMenuItemId: string
  setOpenedMenuItemId: (id: string) => void
  item: MenuItemType
}

let enterTimeout = null
let exitTimeout = null

const MenuItem = ({ openedMenuItemId, setOpenedMenuItemId, item }: MenuItemProps): JSX.Element => {
  const handleMouseMove = () => {
    clearTimeout(enterTimeout)
    clearTimeout(exitTimeout)

    enterTimeout = setTimeout(() => {
      setOpenedMenuItemId(item.id)
    }, 30)
  }

  const handleMouseEnter = () => {
    clearTimeout(enterTimeout)
    clearTimeout(exitTimeout)

    enterTimeout = setTimeout(() => {
      setOpenedMenuItemId(item.id)
    }, 30)
  }

  const handleMouseLeave = () => {
    clearTimeout(enterTimeout)

    exitTimeout = setTimeout(() => {
      setOpenedMenuItemId("")
    }, 100)
  }

  const renderFeatured = () => (
    <FeatureContainer>
      <FeatureContainerInner>
        <Link to={item.featuredLink?.url}>
          <UrlImage image={item.featuredImage} />
        </Link>
        <LinkBox>
          <FeaturedUnderlineButton to={item.featuredLink?.url}>
            {item.featuredLink?.title}
          </FeaturedUnderlineButton>
          <Icon name="angle double right" />
        </LinkBox>
      </FeatureContainerInner>
    </FeatureContainer>
  )

  const subLevels = findSubLevels(item, item)

  return (
    <MenuItemContainer
      key={item.label}
      isHovered={item.id === openedMenuItemId}
      isSmall={subLevels.length === 1}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <TopLink to={cleanUrl(item.url)}>{safeHeDecode(item.label || "")}</TopLink>
      {renderMegaMenu(subLevels) && (
        <ItemSubContainer>
          <ItemSubContentContainer>
            <ItemSubContentSubContainer>
              {subLevels.map((subLevel: MenuItemType, index: number) => {
                const formattedSubLevelUrl = cleanUrl(subLevel.url)

                return (
                  <ColumnContainer key={`${subLevel.url}${index}`}>
                    {subLevels.length > 1 && (
                      <HeaderLink>
                        <UnderlineButton
                          to={cleanUrl(subLevel.url)}
                          disabled={formattedSubLevelUrl === "#"}
                        >
                          {safeHeDecode(subLevel.label || "")}
                        </UnderlineButton>
                      </HeaderLink>
                    )}
                    {subLevel.children.map((childItem: MenuItemType, index: number) => {
                      const formattedChildUrl = cleanUrl(childItem.url)

                      return (
                        <ChildLinkContainer key={`${childItem.url}${index}`}>
                          <ChildLink to={formattedChildUrl}>
                            {safeHeDecode(childItem.label || "")}
                          </ChildLink>
                        </ChildLinkContainer>
                      )
                    })}
                  </ColumnContainer>
                )
              })}
              {item.featuredImage && renderFeatured()}
            </ItemSubContentSubContainer>
          </ItemSubContentContainer>
        </ItemSubContainer>
      )}
    </MenuItemContainer>
  )
}

export default MenuItem
